<template>
  <div class="dashboard-container">
    <div class="flex justify-between items-center mb-6">
      <h2 class="text-2xl font-black">Vue d'ensemble</h2>
      <el-button @click="loadStats" :loading="isLoading" circle :icon="Refresh" />
    </div>

    <div v-if="isLoading" class="loading-state">
      <el-icon class="is-loading"><Loading /></el-icon>
      Initialisation du centre de contrôle…
    </div>

    <el-empty v-else-if="!Object.keys(stats).length" description="Aucune donnée disponible" />

    <template v-if="!isLoading && Object.keys(stats).length">
      <!-- KPI summary grid -->
      <div class="kpi-grid">
        <div v-for="(kpi, index) in topKpis" :key="kpi.key" class="stat-card reveal" :style="{ animationDelay: (index * 0.1) + 's' }">
          <div class="stat-header">
            <div class="stat-icon-box" :style="{ background: kpi.bg }">
              <el-icon :style="{ color: kpi.color }"><component :is="kpi.iconComponent" /></el-icon>
            </div>
            <div class="stat-label">{{ kpi.label }}</div>
          </div>
          <div class="stat-value" :style="{ color: kpi.color }">{{ kpi.value }}</div>
        </div>
      </div>

      <!-- Charts section -->
      <div class="charts-container">
        <el-card shadow="never" class="reveal" style="animation-delay: 0.4s">
          <template #header><div class="card-header-title">Flux des Missions</div></template>
          <div class="chart-wrapper">
            <canvas ref="missionChartCanvas"></canvas>
          </div>
        </el-card>
        
        <el-card shadow="never" class="reveal" style="animation-delay: 0.5s">
          <template #header><div class="card-header-title">Performance Financière</div></template>
          <div class="chart-wrapper">
            <canvas ref="financeChartCanvas"></canvas>
          </div>
        </el-card>
      </div>

      <div class="charts-container secondary">
        <el-card shadow="never" class="reveal" style="animation-delay: 0.6s">
          <template #header><div class="card-header-title">État de la Flotte Artisans</div></template>
          <div class="chart-wrapper">
            <canvas ref="artisanChartCanvas"></canvas>
          </div>
        </el-card>

        <el-card shadow="never" class="reveal" style="animation-delay: 0.7s">
          <template #header><div class="card-header-title">Analyse des Risques & Support</div></template>
          <div class="risk-grid">
            <div v-for="risk in riskKpis" :key="risk.key" class="risk-item">
              <div class="risk-meta">
                <span class="risk-label">{{ risk.label }}</span>
                <el-tag :type="risk.tagType" size="small" effect="dark" round>{{ risk.open }}</el-tag>
              </div>
              <div class="risk-value-row">
                <span class="risk-total">{{ risk.total }}</span>
                <span class="risk-suffix">total</span>
              </div>
              <div class="risk-progress-track">
                <div class="risk-progress-fill" :style="{ width: risk.pct + '%', background: risk.barColor }"></div>
              </div>
            </div>
          </div>
        </el-card>
      </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { Chart, registerables } from 'chart.js'
import { ElMessage } from 'element-plus'
import { Loading, UserFilled, Connection, Lightning, Money, Coin, Clock, Refresh } from '@element-plus/icons-vue'
import { fetchDashboardStats } from '../services/adminApi'
import { formatFcfa } from '../utils/format'
import { extractApiError } from '../api'

Chart.register(...registerables)

const stats = ref({})
const isLoading = ref(false)
const missionChartCanvas = ref(null)
const financeChartCanvas = ref(null)
const artisanChartCanvas = ref(null)

const FCFA = (v) => formatFcfa(v || 0)

const topKpis = computed(() => {
  const s = stats.value
  return [
    { key: 'users', label: 'Utilisateurs', value: (s.total_users || 0).toLocaleString('fr-FR'), iconComponent: UserFilled, color: '#3B82F6', bg: 'rgba(59, 130, 246, 0.1)' },
    { key: 'artisans_online', label: 'En ligne', value: (s.online_artisans || 0).toLocaleString('fr-FR'), iconComponent: Connection, color: '#22C55E', bg: 'rgba(34, 197, 94, 0.1)' },
    { key: 'active', label: 'Missions Actives', value: (s.active_interventions || 0).toLocaleString('fr-FR'), iconComponent: Lightning, color: '#FF6B00', bg: 'rgba(255, 107, 0, 0.1)' },
    { key: 'revenue', label: 'Volume d\'affaires', value: FCFA(s.total_revenue), iconComponent: Money, color: '#22C55E', bg: 'rgba(34, 197, 94, 0.1)' },
    { key: 'commission', label: 'Commissions', value: FCFA(s.commission_total), iconComponent: Coin, color: '#FFB800', bg: 'rgba(255, 184, 0, 0.1)' },
    { key: 'pending_artisans', label: 'En attente', value: (s.pending_artisans || 0).toLocaleString('fr-FR'), iconComponent: Clock, color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.1)' },
  ]
})

const riskKpis = computed(() => {
  const s = stats.value
  const fraudPct = s.total_fraud_alerts ? Math.round((s.open_fraud_alerts / s.total_fraud_alerts) * 100) : 0
  const disputePct = s.total_disputes ? Math.round((s.open_disputes / s.total_disputes) * 100) : 0
  const savPct = s.total_sav_tickets ? Math.round((s.open_sav_tickets / s.total_sav_tickets) * 100) : 0
  return [
    { key: 'fraud', label: 'Fraude', open: s.open_fraud_alerts || 0, total: s.total_fraud_alerts || 0, pct: fraudPct, tagType: (s.open_fraud_alerts || 0) > 0 ? 'danger' : 'success', barColor: '#EF4444' },
    { key: 'disputes', label: 'Litiges', open: s.open_disputes || 0, total: s.total_disputes || 0, pct: disputePct, tagType: (s.open_disputes || 0) > 0 ? 'warning' : 'success', barColor: '#F59E0B' },
    { key: 'sav', label: 'SAV', open: s.open_sav_tickets || 0, total: s.total_sav_tickets || 0, pct: savPct, tagType: (s.open_sav_tickets || 0) > 0 ? 'warning' : 'success', barColor: '#0EA5E9' },
    { key: 'escrow', label: 'Séquestre', open: FCFA(s.pending_escrow), total: FCFA(s.total_revenue), pct: s.total_revenue ? Math.round(((s.pending_escrow || 0) / s.total_revenue) * 100) : 0, tagType: 'info', barColor: '#8B5CF6' },
  ]
})

const buildCharts = async () => {
  await nextTick()
  const s = stats.value

  const gridColor = getComputedStyle(document.documentElement).getPropertyValue('--grid-line').trim()
  const chartDefaults = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: { color: '#94A3B8', boxWidth: 10, padding: 20, font: { size: 11, family: 'Nunito' } }
      }
    }
  }

  if (missionChartCanvas.value) {
    new Chart(missionChartCanvas.value, {
      type: 'doughnut',
      data: {
        labels: ['En attente', 'Actives', 'Terminées', 'Annulées'],
        datasets: [{
          data: [s.pending_requests || 0, s.active_interventions || 0, s.completed_interventions || 0, s.cancelled_interventions || 0],
          backgroundColor: ['#F59E0B', '#FF6B00', '#22C55E', '#64748B'],
          borderWidth: 0,
          hoverOffset: 10
        }],
      },
      options: { ...chartDefaults, cutout: '75%' },
    })
  }

  if (financeChartCanvas.value) {
    new Chart(financeChartCanvas.value, {
      type: 'bar',
      data: {
        labels: ['Libéré', 'Commissions', 'Séquestre'],
        datasets: [{
          data: [s.total_revenue || 0, s.commission_total || 0, s.pending_escrow || 0],
          backgroundColor: ['#22C55E', '#FFB800', '#8B5CF6'],
          borderRadius: 6,
          maxBarThickness: 40,
        }],
      },
      options: {
        ...chartDefaults,
        plugins: { ...chartDefaults.plugins, legend: { display: false } },
        scales: {
          y: { grid: { color: gridColor }, ticks: { color: '#64748B', callback: (v) => v >= 1000 ? (v / 1000) + 'k' : v } },
          x: { grid: { display: false }, ticks: { color: '#64748B' } },
        },
      },
    })
  }

  if (artisanChartCanvas.value) {
    new Chart(artisanChartCanvas.value, {
      type: 'doughnut',
      data: {
        labels: ['Approuvés', 'En attente', 'Rejetés', 'En ligne'],
        datasets: [{
          data: [s.approved_artisans || 0, s.pending_artisans || 0, s.rejected_artisans || 0, s.online_artisans || 0],
          backgroundColor: ['#22C55E', '#F59E0B', '#EF4444', '#3B82F6'],
          borderWidth: 0,
          hoverOffset: 10
        }],
      },
      options: { ...chartDefaults, cutout: '75%' },
    })
  }
}

const loadStats = async () => {
  isLoading.value = true
  try {
    stats.value = await fetchDashboardStats()
    await buildCharts()
  } catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isLoading.value = false }
}

onMounted(loadStats)
</script>

<style scoped>
.dashboard-container { display: flex; flex-direction: column; gap: 1.5rem; }
.loading-state { display: flex; align-items: center; gap: 0.75rem; color: var(--text-muted); padding: 4rem 0; justify-content: center; }

.kpi-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 1.25rem; }
.stat-card { background: var(--glass-bg); border: 1px solid var(--glass-border); border-radius: 16px; padding: 1.25rem; }
.stat-header { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 1rem; }
.stat-value { font-family: 'Outfit', sans-serif; font-size: 1.4rem; font-weight: 900; line-height: 1.1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.stat-icon-box { width: 40px; height: 40px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 1.25rem; }

.charts-container { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
.chart-wrapper { position: relative; height: 280px; display: flex; align-items: center; justify-content: center; }
.card-header-title { font-family: 'Outfit', sans-serif; font-weight: 700; font-size: 1rem; letter-spacing: -0.2px; }

.risk-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.risk-item { background: var(--glass-bg); border: 1px solid var(--glass-border); border-radius: 14px; padding: 1.25rem; }
.risk-meta { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem; }
.risk-label { font-size: 0.75rem; font-weight: 800; text-transform: uppercase; color: var(--text-muted); letter-spacing: 0.5px; }
.risk-value-row { display: flex; align-items: baseline; gap: 0.35rem; }
.risk-total { font-family: 'Outfit', sans-serif; font-size: 1.75rem; font-weight: 900; color: var(--text-main); }
.risk-suffix { font-size: 0.8rem; color: var(--text-dim); font-weight: 600; }
.risk-progress-track { margin-top: 0.75rem; height: 6px; background: var(--glass-bg); border-radius: 10px; overflow: hidden; }
.risk-progress-fill { height: 100%; border-radius: 10px; transition: width 0.5s ease; }

@media (max-width: 1200px) {
  .charts-container { grid-template-columns: 1fr; }
}
@media (max-width: 768px) {
  .risk-grid { grid-template-columns: 1fr; }
}
</style>
